import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/content";
import { StarMark } from "@/components/site/StarMark";

const OmMark = () => <StarMark size={36} />;

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#about", label: t.nav.about },
    { id: "#philosophy", label: t.nav.philosophy },
    { id: "#services", label: t.nav.services },
    { id: "#contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "nav-scrolled" : ""
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12 md:py-5">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <OmMark />
          <span className="heading-serif text-xl leading-none tracking-tight text-forest md:text-2xl">
            {t.brand}
          </span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <Link
              key={l.id}
              to={`/${l.id}`}
              data-testid={`nav-link-${l.id.slice(1)}`}
              className="nav-link text-sm tracking-wide text-ink-soft"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-toggle"
            role="group"
            aria-label="Select language"
            className="lang-toggle"
          >
            <button
              type="button"
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={lang === "en" ? "lang-on" : "lang-off"}
            >
              EN
            </button>
            <span className="lang-sep">/</span>
            <button
              type="button"
              data-testid="lang-hi"
              onClick={() => setLang("hi")}
              className={lang === "hi" ? "lang-on" : "lang-off"}
            >
              हिं
            </button>
            <span className="lang-sep">/</span>
            <button
              type="button"
              data-testid="lang-hinglish"
              onClick={() => setLang("hinglish")}
              className={lang === "hinglish" ? "lang-on" : "lang-off"}
            >
              अ/A
            </button>
          </div>

          <a
            href={CONTACT.tel}
            data-testid="nav-call-btn"
            className="hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm text-sand transition-transform duration-300 hover:-translate-y-0.5 sm:flex"
          >
            <Phone size={15} strokeWidth={1.75} />
            {t.nav.call}
          </a>

          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-bg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <OmMark />
                <span className="heading-serif text-xl text-forest">{t.brand}</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={26} strokeWidth={1.5} className="text-ink" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 pt-10">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  <Link
                    to={`/${l.id}`}
                    onClick={() => setOpen(false)}
                    className="heading-serif block border-b border-line py-5 text-4xl text-ink"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <a
                href={CONTACT.tel}
                onClick={() => setOpen(false)}
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-ink py-4 text-base text-sand"
              >
                <Phone size={17} strokeWidth={1.75} /> {t.nav.call}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
